import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import md5 from 'md5'
import styled, { css } from 'styled-components'
import { addFile, removeFile } from 'actions'
import fileServices from 'services/file'
import { getValueAndFieldByModel } from 'utils/form'
import { mapLanguageToComponentProps } from 'i18n'
import {
  FILE_SIZE_LIMIT,
  ATTACHMENT_TYPE_WHITELIST,
  ATTACHMENT_MIME_TYPE_WHITELIST,
} from 'app-constants'
import { Label, Body } from 'components/styled/typography'
import { Icon } from 'components/styled/icon'
import { CustomTooltip } from 'admin/components/custom-tooltip'
import { ConfirmDelete } from 'components/confirm-delete'
import FormErrors from 'components/form-components/form-errors'
import { Flex } from 'components/styled/containers/flex'

const propTypes = {
  i18n: PropTypes.shape({
    attachments: PropTypes.shape({
      add: PropTypes.string,
      remove: PropTypes.string,
      allowedFileTypes: PropTypes.string,
      placeholderWithDescription: PropTypes.string,
      errors: PropTypes.shape({
        empty: PropTypes.string,
        typeNotWhitelisted: PropTypes.string,
        invalidType: PropTypes.func.isRequired,
        size: PropTypes.string,
      }),
    }),
    jobAdApplication: PropTypes.shape({
      legendAttachments: PropTypes.string,
    }),
  }).isRequired,
  model: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
  value: PropTypes.arrayOf(PropTypes.shape({})),
  dispatch: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  readOnly: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  publicInterview: PropTypes.bool,
  errors: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.shape({}),
  ]),
  valid: PropTypes.bool,
  description: PropTypes.string,
}

const defaultProps = {
  publicInterview: false,
  value: [],
  valid: null,
  readOnly: false,
  isRequired: false,
  onAdd: undefined,
  onRemove: undefined,
  errors: null,
  description: '',
}

const LiStyle = css`
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.space.s40};
  padding: ${({ theme }) => theme.space.s12};

  ${({ theme }) => theme.mediaQuery.phone} {
    height: auto;
  }
`

const StyledAddButton = styled.li`
  ${LiStyle};
  cursor: pointer;
`

const StyledFileItem = styled.li`
  ${LiStyle};
  justify-content: space-between;
`

const AttachmentsWrapper = styled.div`
  input[type='file'] {
    display: none;
  }
`

const LabelWithTooltip = styled(Label)`
  display: flex;
`

const StyledFileName = styled(Body)`
  font-style: italic;
`

const AttatchIcon = styled(Icon).attrs({
  type: 'attachment',
  size: 's20',
})`
  margin-right: ${({ theme }) => theme.space.s16};
`

const StyledInput = styled.ul`
  background-color: ${({ theme }) => theme.color.grey07};
  border: none;
  height: auto;
  min-height: ${({ theme }) => theme.space.s40};
  margin: 0;
  padding: 0;
  list-style: none;

  &[readonly] ${StyledAddButton} > * {
    cursor: default;
  }

  li:nth-child(even) {
    background-color: ${({ theme }) => theme.color.grey05};
  }
`
/**
 * Form element to handle attachment uploads. Files and stored to store
 * and thus addFile and removeFile actions are used.
 */
export class Attachments extends Component {
  componentDidMount() {
    // If we don't have value when this is mount set error or user can
    // submit form without value
    const { dispatch, isRequired, model, value } = this.props
    if (isRequired && value.length === 0) {
      dispatch(actions.setErrors(model, { isRequired: true }))
    }
  }

  componentDidUpdate(prevProps) {
    const { dispatch, isRequired, model, value, errors } = this.props
    const hasErrors = errors && typeof errors === 'object'
    const notWhitelistedErrorRemoved =
      hasErrors &&
      typeof prevProps.errors.typeNotWhitelisted !== 'undefined' &&
      !errors.typeNotWhitelisted &&
      prevProps.errors.typeNotWhitelisted
    // Set isRequred error if no value
    const fieldErrors = typeof errors === 'object' ? errors : {}
    if (
      hasErrors &&
      typeof prevProps.errors.typeNotWhitelisted === 'undefined' &&
      errors.typeNotWhitelisted
    ) {
      this.timeout = setTimeout(() => {
        dispatch(
          actions.setErrors(model, {
            ...this.props.errors,
            typeNotWhitelisted: false,
          })
        )
      }, 3000)
    }
    if (
      isRequired &&
      value &&
      (value.length !== prevProps.value.length || notWhitelistedErrorRemoved)
    ) {
      if (value.length === 0) {
        dispatch(actions.setErrors(model, { isRequired: true }))
      } else {
        dispatch(
          actions.setErrors(model, { ...fieldErrors, isRequired: false })
        )
      }
    }
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  onClick() {
    const { readOnly } = this.props
    const { input } = this.refs
    if (!readOnly) {
      input.click()
    }
  }

  onRemove(key) {
    const { model, dispatch, onRemove } = this.props
    dispatch(actions.filter(model, file => file.key !== key))
    dispatch(actions.setErrors(model, false))
    dispatch(removeFile(key))
    onRemove && onRemove(key)
  }

  onChange({ target }) {
    const { model, value, limit, dispatch, isRequired } = this.props
    const files = [...target.files].slice(0, limit - value.length)
    const { input } = this.refs
    // Reset possible previous validation errors
    if (
      isRequired &&
      files &&
      files.length === 0 &&
      value &&
      value.length === 0
    ) {
      dispatch(actions.setErrors(model, { isRequired: true }))
    } else {
      dispatch(actions.setErrors(model, false))
    }

    files.forEach(file =>
      this.readFile(file)
        .then(url => {
          const key = md5(url + Date.now())
          const { name } = file
          this.addAttachment(name, key, url)
          input.value = ''
        })
        .catch(error => {
          dispatch(actions.setTouched(model))
          dispatch(actions.setErrors(model, error))
        })
    )
  }

  addAttachment(name, key, url) {
    const { onAdd, publicInterview, model, dispatch } = this.props
    if (onAdd && !publicInterview) {
      onAdd({ key, name, datauri: url })
    } else {
      dispatch(addFile(key, url))
      dispatch(actions.push(model, { key, name }))
    }
  }

  readFile(file) {
    const { i18n, errors } = this.props
    const lowerCaseName = file.name.toLowerCase()
    const extension = lowerCaseName.substr(lowerCaseName.lastIndexOf('.') + 1)

    if (file.size > FILE_SIZE_LIMIT) {
      return Promise.reject(i18n.attachments.errors.size)
    }
    if (file.size === 0) {
      return Promise.reject(i18n.attachments.errors.empty)
    }

    if (file.type === 'text/html') {
      return Promise.reject(
        i18n.attachments.errors.invalidType(
          `${file.type.replace(/.*\//, '').toUpperCase()}`
        )
      )
    }

    if (
      !ATTACHMENT_MIME_TYPE_WHITELIST.includes(file.type) ||
      !ATTACHMENT_TYPE_WHITELIST.includes(extension)
    ) {
      const fieldErrors = typeof errors === 'object' ? errors : {}
      return Promise.reject({ ...fieldErrors, typeNotWhitelisted: true })
    }
    return fileServices.readAsDataUrl(file).then(dataUrl => {
      const base64str = dataUrl.substring(dataUrl.indexOf(',') + 1)
      const decoded = atob(base64str)

      if (!decoded || decoded.length > FILE_SIZE_LIMIT) {
        throw i18n.attachments.errors.size
      }
      // readAsDataUrl adds 'data:' to the result, so length must be above 5

      if (dataUrl.length <= 5) {
        throw i18n.attachments.errors.empty
      }
      return dataUrl
    })
  }

  renderFiles() {
    const { value, limit, i18n } = this.props

    return value.slice(0, limit).map(({ key, name }, index) => (
      <StyledFileItem key={`${index}-${key}`}>
        <StyledFileName className="file">{name}</StyledFileName>
        <ConfirmDelete
          size="small"
          showDeleteText
          color="error110"
          item={index}
          onConfirm={() => this.onRemove(key)}
        >
          {i18n.attachments.remove}
        </ConfirmDelete>
      </StyledFileItem>
    ))
  }

  render() {
    const {
      model,
      limit,
      value,
      valid,
      readOnly,
      i18n,
      isRequired,
      description,
    } = this.props
    const className = value.length ? (valid ? ' valid' : ' invalid') : ''

    const { length } = value
    const toolTip = (
      <CustomTooltip id="attachment-type-whitelist">
        {`${
          i18n.attachments.allowedFileTypes
        }: ${ATTACHMENT_TYPE_WHITELIST.join(', ')}`}
      </CustomTooltip>
    )

    const placeholder = description
      ? i18n.attachments.placeholderWithDescription
      : i18n.attachments.add

    return (
      <AttachmentsWrapper className="attachments">
        <LabelWithTooltip color="grey90">
          {`${i18n.jobAdApplication.legendAttachments}${isRequired ? '*' : ''}`}
          {toolTip}
        </LabelWithTooltip>
        {description && (
          <Body mb="s4" fontStyle="italic">
            {description}
          </Body>
        )}
        <StyledInput className={className} readOnly={readOnly}>
          {this.renderFiles()}
          {length < limit ? (
            <StyledAddButton className="add" onClick={() => this.onClick()}>
              <AttatchIcon />
              <Body>{placeholder + (isRequired ? '*' : '')}</Body>
            </StyledAddButton>
          ) : null}
        </StyledInput>
        <Flex justifyContent="space-between" align-items="center">
          <FormErrors
            className="error"
            model={model}
            show={{ touched: true }}
          />
          <Body textAlign="right">{`${length}/${limit}`}</Body>
        </Flex>
        <input
          type="file"
          aria-label="Attachment file input"
          name={model}
          maxLength={limit}
          ref="input"
          multiple
          onChange={event => this.onChange(event)}
        />
      </AttachmentsWrapper>
    )
  }
}

Attachments.propTypes = propTypes
Attachments.defaultProps = defaultProps

export default connect(
  mapLanguageToComponentProps((state, { model }) => {
    const { value, field = {} } = getValueAndFieldByModel(state, model)
    return { value, valid: field.$form && field.$form.valid, errors: field.$form && field.$form.errors }
  })
)(Attachments)
